import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, Loader2 } from "lucide-react";

/**
 * Reusable inline PDF preview modal.
 * Props:
 *  - open, onOpenChange
 *  - fetchBlob: async () => Blob            // caller supplies auth-aware fetch
 *  - filename: string                        // for download attribute
 *  - title?: string
 */
export default function PdfPreviewModal({ open, onOpenChange, fetchBlob, filename, title }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      if (url) { URL.revokeObjectURL(url); }
      setUrl(null); setError(""); setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const blob = await fetchBlob();
        if (!alive) return;
        const u = URL.createObjectURL(blob);
        setUrl(u);
      } catch (e) {
        setError("Impossibile caricare il PDF. Riprova.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const downloadNow = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = filename || "documento.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-[#0F2C59] font-display">
            {title || "Anteprima PDF"} <span className="text-slate-400 font-normal text-sm">· {filename}</span>
          </DialogTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={downloadNow} disabled={!url} data-testid="modal-download-pdf">
              <Download className="w-4 h-4 mr-1" /> Scarica
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} data-testid="modal-close-pdf">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading && (
            <div className="h-full flex items-center justify-center">
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" /> Preparazione anteprima…
              </div>
            </div>
          )}
          {error && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-red-600">
                <div>{error}</div>
              </div>
            </div>
          )}
          {url && !loading && !error && (
            <iframe
              src={url}
              title="PDF preview"
              className="w-full h-full border-0"
              data-testid="pdf-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
